import React, { Suspense, lazy, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './auth/AuthContext';
import { ToastContainer } from './components/Toast';
import { UpdateBanner } from './components/UpdateBanner';
import { LoginModal } from './components/modals/LoginModal';
import FileViewerHost from './components/FileViewer/FileViewerHost';
import { LS } from './utils/storage';
import ChatPage from './pages/ChatPage';
import { applyUiScale } from './utils/uiScale';

// 관리자·다운로드 화면은 필요할 때만 내려받는다 (채팅 첫 로딩 용량 감소)
const DownloadPage = lazy(() => import('./pages/DownloadPage'));
const AdminPage = lazy(() => import('./pages/admin/AdminPage'));

// 로그인이 필요한 화면 공통 처리
function RequireAuth({ children, adminOnly = false, loginHint }) {
    const { status, user } = useAuth();
    if (status === 'checking') return null;
    if (status !== 'authed') return <LoginModal hint={loginHint} defaultTab={adminOnly ? 'account' : undefined} />;
    if (adminOnly && !user.isAdmin) {
        return (
            <div className="page-message">
                <h2>관리자 전용 페이지</h2>
                <p>관리자 계정으로 로그인해야 이용할 수 있습니다.</p>
                <a href="/">채팅으로 돌아가기</a>
            </div>
        );
    }
    return children;
}

const applySavedTheme = () => {
    const saved = LS.get('dark-mode');
    document.body.classList.toggle('dark-mode', saved === null ? true : saved === 'true');
};

export default function App() {
    useEffect(applySavedTheme, []);
    useEffect(() => {
        applyUiScale();
        window.addEventListener('resize', applyUiScale);
        return () => window.removeEventListener('resize', applyUiScale);
    }, []);
    return (
        <AuthProvider>
            <BrowserRouter>
                <ToastContainer />
                <UpdateBanner />
                <FileViewerHost />
                <Suspense fallback={<div className="page-message">불러오는 중...</div>}>
                    <Routes>
                        <Route path="/" element={<RequireAuth><ChatPage /></RequireAuth>} />
                        <Route path="/download" element={<RequireAuth loginHint="다운로드는 로그인 후 이용할 수 있습니다."><DownloadPage /></RequireAuth>} />
                        <Route path="/admin/*" element={<RequireAuth adminOnly loginHint="관리자 계정으로 로그인하세요."><AdminPage /></RequireAuth>} />
                        <Route path="/admin.html" element={<Navigate to="/admin" replace />} />
                        <Route path="/admin-login.html" element={<Navigate to="/admin" replace />} />
                        <Route path="/download.html" element={<Navigate to="/download" replace />} />
                        <Route path="*" element={<Navigate to="/" replace />} />
                    </Routes>
                </Suspense>
            </BrowserRouter>
        </AuthProvider>
    );
}
